import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import {
  Observable,
  switchMap,
  map,
  catchError,
  of,
  take,
  firstValueFrom
} from 'rxjs';
import * as fromResourcesActions from '../actions/resources.actions';
import { FirebaseService } from 'src/app/services/firebase/firebase.service';
import {
  AngularFirestore,
  DocumentReference
} from '@angular/fire/compat/firestore';
import { ResourceFirebaseResponse } from 'src/app/models/resources.model';

@Injectable()
export class ResourcesEffects {
  private readonly collectionName = 'resources';
  constructor(
    private readonly action$: Actions,
    private readonly firebaseService: FirebaseService,
    private readonly firestore: AngularFirestore
  ) {}

  // Load Resources
  loadResources$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(fromResourcesActions.LoadResourcesAction),
      switchMap(({ trainer }) => {
        const trainerRef = this.firestore.doc(`users/${trainer.id}`).ref;

        return this.firebaseService
          .getDataOfCollectionByAnyField(
            this.collectionName,
            'trainerRef',
            '==',
            trainerRef
          )
          .pipe(
            take(1),
            map((value) =>
              value.docs.map((doc) => {
                const resource: ResourceFirebaseResponse = {
                  ...(doc.data() as ResourceFirebaseResponse),
                  id: doc.id
                };
                delete resource.trainerRef;
                return resource;
              })
            )
          );
      }),
      map((data) => ({
        type: fromResourcesActions.LOAD_RESOURCES_SUCCESS,
        data
      })),
      catchError((error) =>
        of({
          type: fromResourcesActions.LOAD_RESOURCES_FAIL,
          error: error
        })
      )
    )
  );

  // Add Resources
  addResources$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(fromResourcesActions.AddResourceAction),
      switchMap(async ({ resource }) => {
        await this.firebaseService.uploadFiles(
          resource.file,
          resource.name,
          `${this.collectionName}/${resource.user.id}`
        );

        const trainerRef = this.firestore.doc(`users/${resource.user.id}`)
          .ref as DocumentReference;
        const resourceToAdd: ResourceFirebaseResponse = {
          name: resource.name,
          trainerRef
        };
        const result = await this.firebaseService.addDoc(
          this.collectionName,
          resourceToAdd
        );
        delete resourceToAdd.trainerRef;

        return { ...resourceToAdd, id: result.id };
      }),
      map((resource) => ({
        type: fromResourcesActions.ADD_RESOURCE_SUCCESS,
        resource
      })),
      catchError((error) =>
        of({
          type: fromResourcesActions.ADD_RESOURCE_FAIL,
          error: error
        })
      )
    )
  );

  // Delete Resources
  deleteResources$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(fromResourcesActions.DeleteResourceAction),
      switchMap(async ({ resource }) => {
        const filepath = `${this.collectionName}/${resource.trainer!.id}/${resource.name}`;
        await firstValueFrom(this.firebaseService.deleteFile(filepath));
        await this.firebaseService.deleteDoc(this.collectionName, resource.id!);
        return resource;
      }),
      map((resource) => ({
        type: fromResourcesActions.DELETE_RESOURCE_SUCCESS,
        resource
      })),
      catchError((error) =>
        of({
          type: fromResourcesActions.DELETE_RESOURCE_FAIL,
          error: error
        })
      )
    )
  );
}
